import ConditionalWrapper from '@atoms/ConditionalWrapper/ConditionalWrapper';
import GridSlider from '@atoms/GridSlider/GridSlider';
import ListingTypeEnum from '@models/enums/ListingTypeEnum';
import SiteEnum from '@models/enums/SiteEnum';
import { IFilterGroup } from '@models/IFilterGroup';
import { IInspirationCard } from '@models/IInspirationCard';
import { IListingFilterGroup, requestState } from '@models/IListing';
import {
  CardType,
  IListingWrapper,
  QueryState,
  SelectedFilter,
} from '@models/IListingWrapper';
import InfiniteScroll from '@molecules/InfiniteScroll/InfiniteScroll';
import ListingColourBar from '@molecules/ListingColourBar/ListingColourBar';
import ListingProductBar from '@molecules/ListingProductBar/ListingProductBar';
import { ListingProvider } from '@utility/context/ListingContext';
import { WishlistButtonContext } from '@utility/context/WishlistButtonContext';
import useBreakpoint from '@utility/hooks/useBreakpoint';
import useListingComponentData from '@utility/hooks/useListingComponentData';
import {
  ActionType,
  initialState,
  reducer,
} from '@utility/reducers/ListingWrapper';
import classNames from 'classnames';
import { AnimatePresence } from 'motion/react';
import queryString from 'query-string';
import {
  MouseEvent,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { getParsedUrlParams, mapBreakpoint } from './config';
import { getGridConfig, sanitizeQuery } from './grid';
import { setCardPositionInGrid } from './helpers/setCardPositionInGrid';
import { useGridAnimation } from './helpers/useGridAnimation';
import ListingFeaturedCard from './ListingFeaturedCard';
import ListingFilterDrawer from './ListingFilterDrawer';
import ListingFilters from './ListingFilters';
import ListingHeader from './ListingHeader';
import ListingMobileFilters from './ListingMobileFilters';
import ListingResultsGrid from './ListingResultsGrid';
import ListingResultsSummary from './ListingResultsSummary';
import ListingLoadMoreButton from './LoadMoreButton';
import SelectedFiltersBar from './SelectedFiltersBar';

const ListingWrapper = ({
  additionalFilters = [],
  applicationAreas,
  colourBar,
  customQueryParams = {},
  featuredInspiration,
  filters = [],
  gridSizeRange,
  initialFilters,
  initialGridSize = 100,
  inspirationCard,
  listingFilterParam,
  listingFilters,
  pageUrl,
  permanentFilters,
  productBar,
  searchEndpoint,
  site,
  type,
  WishlistButton,
  showSearchInListing,
  ...props
}: IListingWrapper) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    listItems,
    slicedListItems,
    selectedFilters,
    sortedListingFilters,
    selectedFeaturedInspiration,
    selectedInspirationCard,
    promoCardReferences,
    clearSearch,
    hotspotData,
    inspirationData,
  } = state;

  const breakpoint = useBreakpoint();
  const resultsRef = useRef<HTMLDivElement>(null);
  const hasPopulatedQueryState = useRef(false);

  const {
    gridSize,
    pendingGridSize,
    isAnimatingGridChange,
    requestGridSizeChange,
    applyPendingGridSize,
  } = useGridAnimation(initialGridSize);

  const displayedSelectedFilters = useMemo(
    () =>
      selectedFilters.filter(
        (filter) => !permanentFilters?.includes(filter.id)
      ),
    [selectedFilters, permanentFilters]
  );

  const subfilters: IFilterGroup[] = filters.flatMap((filter) =>
    filter.options
      .filter((filter) => !!filter.subFilters)
      .map((option) => option.subFilters as IFilterGroup)
  );

  const params =
    typeof window !== 'undefined'
      ? queryString.pick(window?.location.search, [
          'freetext',
          listingFilterParam ?? '',
          ...filters.map((filter) => String(filter.id)),
          ...subfilters.map((filter) => filter.id),
          ...additionalFilters.map((filter) => filter),
        ])
      : '';

  const parsedUrlParams = getParsedUrlParams(params, listingFilterParam ?? '');
  const hasParams = Object.keys(parsedUrlParams).length > 0;
  const existingFilters = hasParams ? parsedUrlParams : initialFilters;
  const initialSearchText = parsedUrlParams ? parsedUrlParams?.freetext : '';

  // Adjust cardVariant, gridClasses and cardSize based on gridSize
  const config = getGridConfig(gridSize, type);

  const { gridClasses, cardVariant, hotspot, inspiration } = config ?? {
    gridClasses: '',
    cardVariant: undefined,
    hotspot: undefined,
    inspiration: undefined,
  };
  // Work out whether to show featured inspiration and inspiration card based on breakpoint and amount of items shown
  type Breakpoint = 'sm' | 'md' | 'xl';

  const handlePosition = (cardType: CardType) =>
    setCardPositionInGrid({ cardType, gridSize, site, type });

  useEffect(() => {
    if ((selectedFeaturedInspiration || selectedInspirationCard) && hotspot) {
      const normalizedBreakpoint = mapBreakpoint(breakpoint as Breakpoint);

      dispatch({
        type: ActionType.HOTSPOT_DATA,
        payload: {
          show:
            (selectedFeaturedInspiration &&
              listItems.length >=
                hotspot.minResultsToShow[normalizedBreakpoint]) ||
            false,
          occupiedSpaceCount:
            hotspot.occupiedSpaceCount[normalizedBreakpoint] || 0,
          aspectRatio: hotspot.aspectRatio[normalizedBreakpoint],
          tabPosition: hotspot.tabPosition[normalizedBreakpoint],
        },
      });

      dispatch({
        type: ActionType.INSPIRATION_DATA,
        payload: {
          show:
            (selectedInspirationCard &&
              listItems.length >=
                inspiration.minResultsToShow[normalizedBreakpoint]) ||
            false,
          occupiedSpaceCount:
            inspiration.occupiedSpaceCount[normalizedBreakpoint] || 0,
          tabPosition: inspiration.tabPosition[normalizedBreakpoint],
        },
      });
    }
  }, [
    selectedFeaturedInspiration,
    selectedInspirationCard,
    breakpoint,
    listItems,
  ]);

  const calculatedPageSize = useMemo(() => {
    const pageSizes = {
      150: breakpoint === 'sm' ? 8 : 12,
      100: breakpoint === 'sm' ? 10 : 20,
      50: breakpoint === 'sm' ? 18 : 36,
      0: breakpoint === 'sm' ? 40 : 144,
    };

    return pageSizes[gridSize];
  }, [gridSize, breakpoint]);

  // Remove hotspot and inspiration occupied space from listItems
  useEffect(() => {
    const count =
      listItems.length -
      (hotspotData.show ? hotspotData.occupiedSpaceCount : 0) -
      (inspirationData.show ? inspirationData.occupiedSpaceCount : 0);

    const newSlicedListItems =
      data.total <= listItems.length || data.count < calculatedPageSize
        ? listItems
        : listItems.slice(0, count);

    dispatch({
      type: ActionType.SLICED_LIST_ITEMS,
      payload: newSlicedListItems,
    });
  }, [listItems, hotspotData, inspirationData]);

  const defaultState = {
    applicationAreas,
    freetext: '',
    page: 0,
    pageUrl,
    take: calculatedPageSize,
    skip: 0,
    promoCardReference: promoCardReferences[0] || 0,
  };

  const [queryState, setQueryState] = useState<QueryState>({
    ...defaultState,
    ...customQueryParams,
  });

  const {
    pageUrl: formPageUrl,
    freetext,
    page,
    take,
    skip,
    ...rest
  } = queryState;

  // Remove any customQueryParams to get the drawerFilters
  const drawerFilters = Object.fromEntries(
    Object.entries(rest).filter(
      ([key]) => !Object.keys(customQueryParams).includes(key)
    )
  );

  const mappedDrawerFilters: SelectedFilter[] = Object.entries(
    drawerFilters
  ).map(([key, value]) => ({
    id: value as string,
    name: key,
    label: `Filter for ${key}`,
  }));

  const setCleanQueryParams = (data: QueryState) => {
    const cleanQuery = sanitizeQuery<QueryState>({
      ...customQueryParams,
      ...data,
      pageUrl,
    });

    // Remove specific parameters
    const {
      applicationAreas,
      page,
      take,
      pageUrl: filteredUrl,
      skip,
      ...filteredQuery
    } = cleanQuery;

    const queryStringified = queryString.stringify(filteredQuery, {
      arrayFormat: 'comma',
    });

    setQueryState(cleanQuery);

    const newUrl = queryStringified
      ? `${window.location.pathname}?${queryStringified}`
      : window.location.pathname;

    window.history.replaceState(null, '', newUrl);
  };

  // Populate queryState using url query params on client side
  useEffect(() => {
    setValue('freetext', parsedUrlParams.freetext);

    setCleanQueryParams({
      ...parsedUrlParams,
      ...drawerFilters,
      ...customQueryParams,
      ...existingFilters,
      page: 0,
      skip: 0,
    });
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm({ defaultValues: queryState });

  const filterIds = filters
    .flatMap((filter) => [
      filter.id,
      ...filter.options.map((option) => option.subFilters?.id as string),
    ])
    .filter(Boolean);
  const checkedDrawerFilters = watch(filterIds);
  const mappedCheckedDrawerFilters = checkedDrawerFilters
    .flatMap((value) => value)
    .filter(Boolean);

  const {
    fetchData,
    state: { data, loading },
  } = useListingComponentData<
    { readonly [key: string]: unknown },
    requestState
  >(searchEndpoint, { results: listItems, total: 0, count: 0 }, false, false);

  // Function to clear all filters and query params
  const clearAllFilters = (
    e: MouseEvent<HTMLButtonElement>,
    params: Partial<typeof queryState> = {}
  ) => {
    e.preventDefault();

    if (checkedDrawerFilters) reset(checkedDrawerFilters);

    // Resets all the current selected filters
    reset(filterIds);
    reset({ freetext: '' });

    dispatch({ type: ActionType.CLEAR_SEARCH, payload: clearSearch + 1 });
    dispatch({ type: ActionType.SORTED_LISTING_FILTERS, payload: [] });
    dispatch({ type: ActionType.MOBILE_FILTERS_OPEN, payload: false });

    setCleanQueryParams({
      freetext: '',
      take: queryState.take,
      page: 0,
      skip: 0,
      promoCardReference: 0,
      applicationAreas,
      ...params,
      ...customQueryParams,
    });
  };

  /**
   * Update the selected inspirational images/cards based on the currently active filter IDs.
   * This ensures the UI reflects the most relevant inspiration when filters are changed or removed.
   */
  const updateSelectedInspiration = (updatedIds: string[]) => {
    const selectedListingFilter = updatedIds
      .slice()
      .reverse()
      .map((id: string) =>
        listingFilters?.find(({ filter: { id: filterId } }) => filterId === id)
      )
      .find(
        (filter?: IListingFilterGroup) =>
          filter && (filter.featuredInspiration || filter.inspirationCard)
      );

    const fallbackFilter = sortedListingFilters[0];

    dispatch({
      type: ActionType.SELECTED_FEATURED_INSPIRATION,
      payload:
        selectedListingFilter?.featuredInspiration ??
        fallbackFilter?.featuredInspiration ??
        featuredInspiration!,
    });

    const renderedInspirationCard =
      type === ListingTypeEnum.Article
        ? data?.promoCard ?? selectedInspirationCard
        : selectedListingFilter?.inspirationCard ??
          fallbackFilter?.inspirationCard ??
          inspirationCard;

    dispatch({
      type: ActionType.SELECTED_INSPIRATION_CARD,
      payload: renderedInspirationCard as IInspirationCard,
    });
  };

  const loadMoreItems = () => {
    setCleanQueryParams({
      ...queryState,
      skip: listItems.length,
      page: queryState.page + 1,
    });
  };

  // Form submission handler
  const onSubmit: SubmitHandler<QueryState> = async (data, e) => {
    e?.preventDefault();

    const {
      applicationAreas,
      page,
      pageUrl,
      freetext,
      promoCardReference,
      skip,
      ...selectedFilters
    } = data;

    // Get additional filters from data based on the additionalFilters keys passed from parent
    const additionalFiltersData = additionalFilters.reduce(
      (acc: Record<string, string[]>, key) => {
        if (data[key] !== undefined) {
          acc[key] = data[key];
        }
        return acc;
      },
      {}
    );

    // Reset filters when a new search term is submitted
    if (!!freetext && freetext !== queryState.freetext) {
      setCleanQueryParams({
        freetext,
        page: 0,
        skip: 0,
        promoCardReference: 0,
        take: queryState.take,
        applicationAreas,
        ...customQueryParams,
        ...additionalFiltersData,
      });
    } else {
      setCleanQueryParams({
        ...queryState,
        ...data,
        page: 0,
        skip: 0,
        promoCardReference,
      });
    }

    if (Object.keys(selectedFilters).length > 0) {
      window.dataLayer.push({
        event: 'trackingEvent_filter_select',
        trackingEvent_filter_select: { ...selectedFilters },
      });
    }

    // Scroll to listing results
    const headerHeight = document.getElementById('header')?.offsetHeight || 0;

    if (resultsRef.current) {
      const resultsOffset =
        resultsRef.current.getBoundingClientRect().top +
        window.scrollY -
        headerHeight;

      window.scrollTo({
        top: resultsOffset,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const updatedPageSize = calculatedPageSize;
    const cleanQuery = sanitizeQuery<QueryState>(queryState);

    if (hasPopulatedQueryState.current) {
      const {
        primaryTags,
        page,
        pageUrl,
        freetext,
        promoCardReference,
        skip,
        applicationAreas,
        ...rest
      } = cleanQuery;

      const fetchPayload = {
        ...(type === ListingTypeEnum.Article
          ? {
              primaryTags,
              secondaryCategoryTags: Object.values(rest).flat(),
            }
          : rest),
        freetext,
        page,
        promoCardReference,
        skip,
        pageUrl,
        applicationAreas,
        take: updatedPageSize,
      };

      fetchData(fetchPayload);
    }

    // Reset with the current queryState
    reset(queryState);

    const { freetext } = queryState;
    const updatedSelectedFreetext = freetext
      ? [{ name: 'freetext', id: 'freetext', label: freetext }]
      : [];

    const getUpdatedSelectedFilters = (filterGroup: IFilterGroup[]) =>
      filterGroup?.flatMap((filter) => {
        const mappedFilter = mappedDrawerFilters.find(
          (mapped) => mapped.name === filter.id
        );

        if (!mappedFilter) return [];

        // Map the options where the id of the option matches one of the ids in the mappedFilter.id array
        return filter.options
          .filter((option) => mappedFilter.id.includes(option.id))
          .map(({ id, label, promoCardReference }) => ({
            name: filter.id,
            id,
            label,
            promoCardReference,
          }));
      });
    const subFilters = filters
      .flatMap((filter) => filter.options.map((option) => option.subFilters))
      .filter((filter) => !!filter);
    // Flatten and transform the filters based on selected filter values
    const updatedSelectedFilters =
      subFilters.length > 0
        ? [
            ...getUpdatedSelectedFilters(filters),
            ...getUpdatedSelectedFilters(subFilters),
          ]
        : getUpdatedSelectedFilters(filters);

    // Match listing filters with query state
    const findMatchingItems = (
      items: any[],
      idAccessor: { (item: any): any; (arg0: any): any }
    ) => {
      // Extract all arrays from queryState
      const queryArrays = Object.values(queryState).filter((value) =>
        Array.isArray(value)
      );

      const queryIds = queryArrays.flat().map(String);

      // Filter items by matching IDs
      return items.filter((item) =>
        queryIds.includes(String(idAccessor(item)))
      );
    };

    const selectedListingFilters = listingFilters
      ? findMatchingItems(listingFilters, (item) => item.filter.id).reduce(
          (acc, item) => {
            acc.push({
              name: listingFilterParam,
              id: item.filter.id.toString(),
              label: item.filter.label,
              featuredInspiration: item.featuredInspiration || null,
              inspirationCard: item.inspirationCard || null,
            });
            return acc;
          },
          []
        )
      : undefined;

    // Set all selected filters
    dispatch({
      type: ActionType.SELECTED_FILTERS,
      payload: [
        ...(updatedSelectedFilters || []),
        ...updatedSelectedFreetext,
        ...(selectedListingFilters || []),
      ],
    });
  }, [queryState]);

  const searchQueryHasNoResults = useMemo(
    () =>
      selectedFilters.length === 1 &&
      !!queryState.freetext &&
      !!data.facets?.length &&
      !data.results.length,
    [selectedFilters, queryState, data]
  );

  // Update results
  useEffect(() => {
    hasPopulatedQueryState.current = true;
    if (queryState.page === 0) {
      dispatch({
        type: ActionType.LIST_ITEMS,
        payload: data.results ?? [],
      });
    } else {
      dispatch({
        type: ActionType.APPEND_LIST_ITEMS,
        payload: data.results ?? [],
      });
    }

    if (data?.results && window.dataLayer && queryState.freetext) {
      window.dataLayer.push({
        event: 'trackingEvent_search_results',
        event_name_variable: 'search_results',
        trackingEvent_search_results: {
          search_type: type,
          search_term: queryState.freetext,
          search_results: data.results.length > 0,
        },
      });
    }

    const selectedIds = listingFilterParam
      ? queryState[listingFilterParam] ?? []
      : [];
    updateSelectedInspiration(selectedIds);
  }, [data]);
  // Fetch new results if grid size changes
  useEffect(() => {
    setQueryState((prev) => ({
      ...prev,
      page: 0,
    }));
  }, [gridSize]);

  useEffect(() => {
    dispatch({
      type: ActionType.SHOW_LOAD_MORE,
      payload: data?.total > slicedListItems.length,
    });
  }, [slicedListItems]);

  // Hotspot card
  const featuredHotspotCard = !!selectedFeaturedInspiration &&
    hotspotData.show && (
      <ListingFeaturedCard
        cardType="hotspot"
        selectedFeaturedInspiration={selectedFeaturedInspiration}
        hotspotData={hotspotData}
        site={site}
        handlePosition={handlePosition}
      />
    );
  //Inspiration card
  const featuredInspirationCard = !!selectedInspirationCard &&
    inspirationData.show && (
      <ListingFeaturedCard
        cardType="inspiration"
        selectedInspirationCard={selectedInspirationCard}
        inspirationData={inspirationData}
        site={site}
        handlePosition={handlePosition}
      />
    );
  return (
    <WishlistButtonContext.Provider value={WishlistButton ?? null}>
      <ListingProvider state={state} dispatch={dispatch}>
        <section className="tw-relative">
          <div className="tw-flex tw-flex-col">
            {type !== ListingTypeEnum.Article && (
              <ListingHeader
                {...props}
                errors={errors}
                initialSearchText={initialSearchText}
                listingFilterParam={listingFilterParam}
                listingFilters={listingFilters}
                queryState={queryState}
                register={register}
                setValue={setValue}
                site={site}
                type={type}
                handleFormSubmit={handleSubmit(onSubmit)}
                onAutoCompleteSubmit={(value: string) =>
                  onSubmit({
                    ...queryState,
                    freetext: value,
                  })
                }
                showSearch={!showSearchInListing}
                setCleanQueryParams={setCleanQueryParams}
              />
            )}
            <div
              ref={resultsRef}
              className="dark tw-col-span-full tw-min-h-[700px]"
            >
              <ListingMobileFilters
                {...props}
                disabled={searchQueryHasNoResults}
                filters={filters}
                initialSearchText={initialSearchText}
                errors={errors}
                handleFormSubmit={handleSubmit(onSubmit)}
                register={register}
                setValue={setValue}
                showSearchInListing={showSearchInListing}
                onAutoCompleteSubmit={(value: string) =>
                  onSubmit({
                    ...queryState,
                    freetext: value,
                  })
                }
              />

              <div className="tw-container tw-flex tw-flex-col tw-items-end tw-justify-between tw-gap-sm tw-pb-xs md:tw-py-sm lg:tw-flex-row lg:tw-pb-md lg:tw-pt-lg">
                <AnimatePresence mode="wait">
                  {!!filters.length && (
                    <div className="tw-relative tw-mb-auto tw-flex tw-w-full tw-flex-col tw-gap-xs md:tw-justify-end lg:tw-items-start">
                      <ListingFilters
                        {...props}
                        disabled={searchQueryHasNoResults}
                        errors={errors}
                        handleFormSubmit={handleSubmit(onSubmit)}
                        initialSearchText={initialSearchText}
                        register={register}
                        setValue={setValue}
                        site={site}
                        showSearchInListing={showSearchInListing}
                        onAutoCompleteSubmit={(value: string) =>
                          onSubmit({
                            ...queryState,
                            freetext: value,
                          })
                        }
                      />
                      {/* Quick Filters */}
                      {!!displayedSelectedFilters.length && (
                        <SelectedFiltersBar
                          {...props}
                          displayedSelectedFilters={displayedSelectedFilters}
                          listingFilterParam={listingFilterParam}
                          onClearAll={clearAllFilters}
                          queryState={queryState}
                          setCleanQueryParams={setCleanQueryParams}
                          setValue={setValue}
                          site={site}
                          updateSelectedInspiration={updateSelectedInspiration}
                        />
                      )}
                    </div>
                  )}
                </AnimatePresence>

                <div
                  className={classNames(
                    'tw-relative tw-flex tw-w-full tw-shrink-0 tw-justify-between tw-gap-sm md:tw-flex-row md:tw-items-center lg:tw-w-auto lg:tw-flex-col lg:tw-items-end lg:tw-justify-end xl:tw-gap-xs',
                    gridSizeRange && gridSizeRange.length < 3
                      ? 'tw-flex-row'
                      : 'tw-flex-col'
                  )}
                >
                  <ListingResultsSummary
                    {...props}
                    loading={loading}
                    hasPopulatedQueryState={hasPopulatedQueryState.current}
                    totalResults={data.total}
                  />

                  {type === ListingTypeEnum.Colour && (
                    <GridSlider
                      {...props}
                      value={pendingGridSize ?? gridSize}
                      setValue={requestGridSizeChange}
                      range={gridSizeRange}
                      defaultSize={100}
                    />
                  )}
                </div>
              </div>
              {colourBar?.colourCard && (
                <div className="tw-container tw-mb-14 xl:tw-mb-[84px]">
                  <ListingColourBar {...colourBar} />
                </div>
              )}
              {productBar?.productCard && (
                <div className="tw-container tw-mb-14 xl:tw-mb-[84px]">
                  <ListingProductBar {...productBar} />
                </div>
              )}
              {slicedListItems && (
                <ConditionalWrapper
                  condition={site === SiteEnum.B2B}
                  wrapper={(children) => (
                    <InfiniteScroll
                      {...props}
                      isLoading={loading}
                      isLastPage={data?.total <= slicedListItems.length}
                      onLoadMore={loadMoreItems}
                    >
                      {children}
                    </InfiniteScroll>
                  )}
                >
                  <ListingResultsGrid
                    {...props}
                    applyPendingGridSize={applyPendingGridSize}
                    cardVariant={cardVariant!}
                    errors={errors}
                    featuredHotspotCard={featuredHotspotCard}
                    featuredInspirationCard={featuredInspirationCard}
                    freetext={queryState.freetext}
                    gridClasses={gridClasses}
                    gridSize={gridSize}
                    hasMoreItemsToShow={state.showLoadMore}
                    hasPopulated={hasPopulatedQueryState.current}
                    hotspotIndex={hotspotData.tabPosition}
                    inspirationIndex={inspirationData.tabPosition}
                    isAnimatingGridChange={isAnimatingGridChange}
                    loading={loading}
                    onLoadMore={loadMoreItems}
                    page={queryState.page}
                    positionClass={handlePosition(
                      type === ListingTypeEnum.Colour ? 'colour' : 'product'
                    )}
                    site={site}
                    type={type}
                  />
                </ConditionalWrapper>
              )}
              {/* Load more button */}
              {!!slicedListItems.length && site === SiteEnum.Deco && (
                <ListingLoadMoreButton
                  {...props}
                  loading={loading}
                  totalResults={data.total}
                  onLoadMore={() => {
                    setQueryState((prev) => ({
                      ...prev,
                      page: prev.page + 1,
                    }));
                  }}
                />
              )}
            </div>
          </div>
          {/* Drawer filters */}
          {!!filters.length && (
            <ListingFilterDrawer
              {...props}
              data={data}
              filters={filters}
              handleFormSubmit={(e: MouseEvent<HTMLElement>) => {
                e.preventDefault();
                handleSubmit(onSubmit)(e);
                dispatch({
                  type: ActionType.MOBILE_FILTERS_OPEN,
                  payload: false,
                });
              }}
              onClearAll={clearAllFilters}
              listingFilterParam={listingFilterParam}
              mappedCheckedDrawerFilters={mappedCheckedDrawerFilters}
              permanentFilters={permanentFilters}
              queryState={queryState}
              register={register}
              setValue={setValue}
              site={site}
              type={type}
              watch={watch}
            />
          )}
        </section>
      </ListingProvider>
    </WishlistButtonContext.Provider>
  );
};

export default ListingWrapper;
